import React, { useState, useRef, FC } from "react";
import { useSpring, animated } from "@react-spring/web";

import { renderToContainer, ShouldRender, NativeProps } from "@/utils/components-utils";

import useIsomorphicLayoutEffect from "@/hooks/useIsomorphicLayoutEffect";
import useUnmountedRef from "@/hooks/useUnmountedRef";
import { useLockScroll } from "@/hooks/useLockScroll";

interface Props extends NativeProps {
	visible?: boolean;
	onClose?: () => void;
	onClick?: () => void;
	children?: React.ReactNode;
}

export const ToastContainer: FC<Props> = (props) => {
	const [active, setActive] = useState(props.visible);
	useIsomorphicLayoutEffect(() => {
		if (props.visible) {
			setActive(true);
		}
	}, [props.visible]);

	const ref = useRef<HTMLDivElement>(null);
	useLockScroll(ref, active ? "strict" : false);

	const unmountedRef = useUnmountedRef();
	const { percent } = useSpring({
		percent: props.visible ? 0 : 100,
		config: {
			precision: 0.1,
			mass: 0.4,
			tension: 300,
			friction: 30,
		},
		onRest: () => {
			if (unmountedRef.current) return;
			setActive(props.visible || false);
		},
	});

	const node = (
		<div style={{ display: active ? undefined : "none" }}>
			<animated.div
				className="toast"
				style={{
					opacity: percent.to((v) => 1 - v),
				}}
				ref={ref}
			>
				{props.children}
			</animated.div>
		</div>
	);

	return (
		<ShouldRender active={active || false} forceRender={true} destroyOnClose={true}>
			{renderToContainer(() => document.body, node)}
		</ShouldRender>
	);
};
